import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { COMPANY, BRANDS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", product: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, inquiry_type: "contact" });
      setSent(true);
      toast.success(lang === "de" ? "Anfrage gesendet. Wir melden uns zeitnah." : "Inquiry sent. We will get back to you shortly.");
      setForm({ name: "", company: "", email: "", phone: "", product: "", message: "" });
    } catch (err) {
      toast.error(lang === "de" ? "Fehler beim Senden. Bitte versuchen Sie es erneut." : "Sending failed. Please try again.");
    } finally { setLoading(false); }
  };

  const info = [
    { icon: Phone, label: lang === "de" ? "Telefon" : "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
    { icon: Mail, label: "E-Mail", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: lang === "de" ? "Adresse" : "Address", value: `${COMPANY.street}, ${COMPANY.zip} ${COMPANY.city}` },
    { icon: Clock, label: lang === "de" ? "Öffnungszeiten" : "Opening hours", value: COMPANY.hours },
  ];

  return (
    <>
      <Seo title="Kontakt – TPU Anfrage & Beratung | PlastoNord GmbH"
        description="Kontaktieren Sie PlastoNord GmbH für TPU Granulat, technische Kunststoffe, Muster- und Angebotsanfragen. Telefon, E-Mail und Anfrageformular."
        path="/kontakt" />
      <section className="border-b border-[#e4e9f0] bg-[#f6f8fb] grain">
        <div className="pn-container py-16 md:py-20">
          <Reveal><p className="overline">{t("nav.contact")}</p>
            <h1 className="mt-4 font-head text-4xl font-light tracking-tight text-[#16181D] md:text-5xl">{lang === "de" ? "Sprechen Sie mit unseren Materialexperten" : "Talk to our material experts"}</h1>
            <p className="mt-5 max-w-2xl text-lg text-[#4a4d52]">{lang === "de" ? "Beratung zur TPU-Auswahl, Muster, Angebote und technische Unterstützung – wir antworten in der Regel innerhalb eines Werktages." : "Advice on TPU selection, samples, quotes and technical support – we usually respond within one business day."}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="pn-container grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="space-y-5">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-4 rounded-xl border border-[#e4e9f0] p-5">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-[#FBEADD] text-[#16181D]"><it.icon size={19} /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#4a4d52]">{it.label}</p>
                    {it.href ? <a href={it.href} className="font-head text-lg font-medium text-[#16181D] hover:text-[#E2611C]">{it.value}</a> : <p className="font-head text-lg font-medium text-[#16181D]">{it.value}</p>}
                  </div>
                </div>
              ))}
              <div className="rounded-xl bg-[#16181D] p-5 text-sm text-white/70">{COMPANY.region} · {t("common.madeIn")}</div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div className="grid h-full place-items-center rounded-2xl border border-[#e4e9f0] p-10 text-center" data-testid="contact-success">
                <div>
                  <CheckCircle2 size={48} className="mx-auto text-[#E2611C]" />
                  <h2 className="mt-4 font-head text-2xl font-medium text-[#16181D]">{lang === "de" ? "Vielen Dank!" : "Thank you!"}</h2>
                  <p className="mt-2 text-[#4a4d52]">{lang === "de" ? "Ihre Anfrage wurde übermittelt." : "Your inquiry has been submitted."}</p>
                  <button onClick={() => setSent(false)} className="btn-ghost mt-6">{lang === "de" ? "Neue Anfrage" : "New inquiry"}</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-[#e4e9f0] p-7 md:p-9" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={lang === "de" ? "Name *" : "Name *"} value={form.name} onChange={upd("name")} required testid="field-name" />
                  <Field label={lang === "de" ? "Unternehmen" : "Company"} value={form.company} onChange={upd("company")} testid="field-company" />
                  <Field label="E-Mail *" type="email" value={form.email} onChange={upd("email")} required testid="field-email" />
                  <Field label={lang === "de" ? "Telefon" : "Phone"} value={form.phone} onChange={upd("phone")} testid="field-phone" />
                </div>
                <div className="mt-5">
                  <label className="mb-1.5 block text-sm font-medium text-[#16181D]">{lang === "de" ? "Produkt / Material" : "Product / Material"}</label>
                  <select value={form.product} onChange={upd("product")} data-testid="field-product"
                    className="w-full rounded-lg border border-[#e4e9f0] bg-white px-4 py-3 text-[#14171c] outline-none focus:border-[#E2611C] focus:ring-2 focus:ring-[#E2611C]/20">
                    <option value="">{lang === "de" ? "Bitte wählen…" : "Please select…"}</option>
                    {BRANDS.map((b) => <option key={b.sku} value={b.sku}>{b[lang].name}</option>)}
                  </select>
                </div>
                <div className="mt-5">
                  <label className="mb-1.5 block text-sm font-medium text-[#16181D]">{lang === "de" ? "Ihre Nachricht *" : "Your message *"}</label>
                  <textarea value={form.message} onChange={upd("message")} required rows={5} data-testid="field-message"
                    className="w-full rounded-lg border border-[#e4e9f0] bg-white px-4 py-3 text-[#14171c] outline-none focus:border-[#E2611C] focus:ring-2 focus:ring-[#E2611C]/20"
                    placeholder={lang === "de" ? "Shore-Härte, Verfahren, Menge, Anwendung…" : "Shore hardness, process, quantity, application…"} />
                </div>
                <button type="submit" disabled={loading} className="btn-primary mt-7 w-full justify-center disabled:opacity-60" data-testid="contact-submit">
                  {loading ? (lang === "de" ? "Senden…" : "Sending…") : <>{t("cta.send")} <Send size={16} /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#16181D]">{label}</label>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full rounded-lg border border-[#e4e9f0] bg-white px-4 py-3 text-[#14171c] outline-none focus:border-[#E2611C] focus:ring-2 focus:ring-[#E2611C]/20" />
    </div>
  );
}
